import clsx from "clsx";
import type { ReactNode } from "react";
import styles from "./Card.module.css";

export interface CardProps {
  title?: ReactNode;
  description?: ReactNode;
  /** Buttons or links aligned with the title. */
  actions?: ReactNode;
  /** Heading level for the title (default 2). */
  headingLevel?: 2 | 3 | 4;
  /** "flush" removes the body padding (a DataTable inside the card). */
  padding?: "default" | "flush";
  children?: ReactNode;
  className?: string;
}

/** A bordered container. Separate groups with space first and a rule second; use a Card last, never nested. */
export function Card({
  title,
  description,
  actions,
  headingLevel = 2,
  padding = "default",
  children,
  className,
}: CardProps) {
  const Heading = `h${headingLevel}` as const;
  return (
    <section className={clsx(styles.card, className)} data-padding={padding}>
      {title || actions ? (
        <header className={styles.header}>
          <div className={styles.heading}>
            {title ? <Heading className={styles.title}>{title}</Heading> : null}
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </header>
      ) : null}
      {children !== undefined ? <div className={styles.body}>{children}</div> : null}
    </section>
  );
}
